/**
 * V3 — Dépenses. Liste expenses + chart par catégorie.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { Download, Search, ChevronLeft, ChevronRight, Receipt, BarChart3, Plus, Pencil, Trash2, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';

const baseURL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');
const PIE_COLORS = ['#f59e0b', '#10b981', '#06b6d4', '#8b5cf6', '#fb923c', '#ec4899', '#84cc16', '#f43f5e', '#3b82f6', '#a855f7'];

const EMPTY_EXP_FORM = {
  date: new Date().toISOString().slice(0, 10),
  libelle: '',
  categorie: 'Exploitation',
  beneficiaire: '',
  montant: '',
  notes: '',
};
const COMMON_CATS = ['Exploitation', 'Carburant', 'Salaire', 'Maintenance', 'Loyer', 'Électricité', 'Eau', 'Téléphone', 'Internet', 'Fournitures', 'Transport', 'Autres'];

export default function V3Expenses() {
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0, kpis: { total_amount: 0, count: 0 }, categories_chart: [], categories_available: [] });
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // CRUD state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_EXP_FORM);
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_EXP_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (exp) => {
    setEditingId(exp.id);
    setFormData({
      date: exp.date || new Date().toISOString().slice(0, 10),
      libelle: exp.libelle || '',
      categorie: exp.categorie || 'Non catégorisé',
      beneficiaire: exp.beneficiaire || '',
      montant: exp.montant ? String(exp.montant) : '',
      notes: exp.notes || '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormError(null);
  };

  const submitForm = async (e) => {
    e?.preventDefault?.();
    if (!formData.libelle.trim()) { setFormError('Le libellé est obligatoire.'); return; }
    if (!formData.date) { setFormError('La date est obligatoire.'); return; }
    const montantNum = Number(formData.montant);
    if (!montantNum || montantNum <= 0) { setFormError('Le montant doit être > 0.'); return; }
    setBusy(true);
    setFormError(null);
    try {
      const payload = {
        date: formData.date,
        libelle: formData.libelle.trim(),
        categorie: formData.categorie || 'Non catégorisé',
        beneficiaire: formData.beneficiaire.trim() || null,
        montant: montantNum,
        notes: formData.notes.trim() || null,
      };
      if (editingId) {
        await api.put(`/api/v3/expenses/${editingId}`, payload);
        showToast(`Dépense « ${payload.libelle} » mise à jour`);
      } else {
        await api.post('/api/v3/expenses', payload);
        showToast(`Dépense « ${payload.libelle} » créée`);
      }
      closeForm();
      fetchData();
    } catch (e) {
      setFormError(e?.response?.data?.detail || e.message || 'Erreur réseau');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    setBusy(true);
    try {
      const exp = data.items.find(x => x.id === confirmDeleteId);
      await api.delete(`/api/v3/expenses/${confirmDeleteId}`);
      showToast(`« ${exp?.libelle || 'Dépense'} » supprimée`);
      setConfirmDeleteId(null);
      fetchData();
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportUrl = () => {
    const p = new URLSearchParams();
    if (from) p.set('from_date', from);
    if (to) p.set('to_date', to);
    return `${baseURL}/api/v3/expenses/export.xlsx?${p}`;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 40 };
      if (category) params.category = category;
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (search) params.search = search;
      const { data: d } = await api.get('/api/v3/expenses', { params });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [category, from, to, search, page]);

  useEffect(() => { Promise.resolve().then(fetchData); }, [fetchData]);

  return (
    <div className="v3-page" data-testid="v3-expenses">
      <div className="v3-page-head" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1>Dépenses</h1>
          <p>{data.total} dépense{data.total>1?'s':''} · total {fmt(data.kpis.total_amount)} FCFA</p>
        </div>
        <button
          onClick={openCreate}
          className="v3-btn v3-btn-primary"
          data-testid="expenses-new-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <Plus size={16} /> Nouvelle dépense
        </button>
      </div>

      <section className="v3-grid-2">
        <div className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <KpiCard icon={<Receipt/>} label="Total dépenses" value={fmt(data.kpis.total_amount) + ' FCFA'} accent="rose" />
          <KpiCard icon={<BarChart3/>} label="Nb opérations" value={data.kpis.count} accent="cyan" />
        </div>

        <div className="v3-card">
          <div className="v3-card-head">
            <h2>Répartition par catégorie</h2>
            <span className="v3-card-sub">Top 8 catégories</span>
          </div>
          <div className="v3-card-body" style={{ height: 220 }}>
            {data.categories_chart.length === 0 ? (
              <div className="v3-empty">Aucune donnée à afficher</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.categories_chart.slice(0, 8)} dataKey="amount" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {data.categories_chart.slice(0, 8).map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                    formatter={(v) => [fmt(v) + ' FCFA']}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </section>

      <section className="v3-filters">
        <div className="v3-filter-grp">
          <label>Catégorie</label>
          <select value={category} onChange={(e) => { setPage(1); setCategory(e.target.value); }} data-testid="filter-category">
            <option value="">Toutes</option>
            {data.categories_available.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Du</label>
          <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} />
        </div>
        <div className="v3-filter-grp">
          <label>Au</label>
          <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} />
        </div>
        <div className="v3-filter-grp v3-filter-search">
          <label>Recherche</label>
          <div className="v3-search-input">
            <Search size={14}/>
            <input type="text" placeholder="Libellé, bénéficiaire…" value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
          </div>
        </div>
        <a href={exportUrl()} className="v3-btn v3-btn-primary"><Download size={16}/>Excel</a>
      </section>

      <section className="v3-card v3-card-table">
        <div className="v3-table-wrap">
          <table className="v3-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Libellé</th>
                <th>Catégorie</th>
                <th>Bénéficiaire</th>
                <th className="num">Montant</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} className="v3-row" data-testid={`expense-row-${e.id}`}>
                  <td className="mono">{e.date}</td>
                  <td>{e.libelle || '—'}</td>
                  <td><span className="v3-pill v3-pill-cat">{e.categorie}</span></td>
                  <td>{e.beneficiaire || '—'}</td>
                  <td className="num strong negative">-{fmt(e.montant)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => openEdit(e)}
                      title="Modifier"
                      aria-label="Modifier"
                      data-testid={`edit-expense-${e.id}`}
                      className="v3-icon-btn"
                      style={{ marginRight: 4 }}
                    ><Pencil size={13} /></button>
                    <button
                      onClick={() => setConfirmDeleteId(e.id)}
                      title="Supprimer"
                      aria-label="Supprimer"
                      data-testid={`delete-expense-${e.id}`}
                      className="v3-icon-btn v3-icon-btn-danger"
                    ><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && <tr><td colSpan={6} className="v3-empty">Aucune dépense.</td></tr>}
              {loading && <tr><td colSpan={6} className="v3-empty">Chargement…</td></tr>}
            </tbody>
          </table>
        </div>
        {data.total_pages > 1 && (
          <div className="v3-pagination">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}><ChevronLeft size={16}/>Préc.</button>
            <span>Page {page} / {data.total_pages}</span>
            <button onClick={() => setPage(p => Math.min(data.total_pages, p+1))} disabled={page >= data.total_pages}>Suiv.<ChevronRight size={16}/></button>
          </div>
        )}
      </section>

      {/* Modal de création/édition */}
      {showForm && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}>
          <form className="v3-modal" onSubmit={submitForm} data-testid="expense-form">
            <div className="v3-modal-head">
              <h2>{editingId ? 'Modifier une dépense' : 'Nouvelle dépense'}</h2>
              <button type="button" onClick={closeForm} aria-label="Fermer" className="v3-icon-btn"><X size={16} /></button>
            </div>
            <div className="v3-modal-body">
              <div className="v3-form-row">
                <ExpFormField label="Date *">
                  <input
                    type="date" required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="expense-form-date"
                  />
                </ExpFormField>
                <ExpFormField label="Montant (FCFA) *">
                  <input
                    type="number" min="0" step="1" required
                    value={formData.montant}
                    onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                    placeholder="0"
                    data-testid="expense-form-montant"
                  />
                </ExpFormField>
              </div>
              <ExpFormField label="Libellé *">
                <input
                  type="text" required
                  value={formData.libelle}
                  onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                  placeholder="Ex: Achat fournitures de bureau"
                  data-testid="expense-form-libelle"
                  autoFocus
                />
              </ExpFormField>
              <div className="v3-form-row">
                <ExpFormField label="Catégorie">
                  <select
                    value={formData.categorie}
                    onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                    data-testid="expense-form-categorie"
                  >
                    {COMMON_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </ExpFormField>
                <ExpFormField label="Bénéficiaire">
                  <input
                    type="text"
                    value={formData.beneficiaire}
                    onChange={(e) => setFormData({ ...formData, beneficiaire: e.target.value })}
                    placeholder="Nom du fournisseur ou bénéficiaire"
                    data-testid="expense-form-beneficiaire"
                  />
                </ExpFormField>
              </div>
              <ExpFormField label="Notes">
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes facultatives, références, n° de facture…"
                  rows={2}
                  data-testid="expense-form-notes"
                />
              </ExpFormField>
              {formError && (
                <div className="v3-form-error">
                  <AlertTriangle size={14} /> {formError}
                </div>
              )}
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={closeForm} className="v3-btn v3-btn-secondary" data-testid="expense-form-cancel">Annuler</button>
              <button type="submit" disabled={busy} className="v3-btn v3-btn-primary" data-testid="expense-form-submit">
                {busy ? 'Enregistrement…' : (editingId ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirmation suppression */}
      {confirmDeleteId && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDeleteId(null); }}>
          <div className="v3-modal v3-modal-confirm">
            <div className="v3-modal-body" style={{ textAlign: 'center' }}>
              <AlertTriangle size={36} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
              <h2 style={{ marginBottom: 6 }}>Confirmer la suppression</h2>
              <p style={{ color: '#94a3b8', fontSize: 13 }}>
                {(() => {
                  const exp = data.items.find(x => x.id === confirmDeleteId);
                  return `Supprimer la dépense « ${exp?.libelle || confirmDeleteId} » du ${exp?.date || '?'} ? Cette action est synchronisée avec l'app legacy.`;
                })()}
              </p>
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={() => setConfirmDeleteId(null)} className="v3-btn v3-btn-secondary">Annuler</button>
              <button type="button" onClick={confirmDelete} disabled={busy} className="v3-btn v3-btn-danger" data-testid="confirm-delete-expense-btn">
                {busy ? 'Suppression…' : 'Oui, supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`v3-toast v3-toast-${toast.type}`} data-testid="expense-toast">
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span>{toast.text}</span>
        </div>
      )}
    </div>
  );
}

function ExpFormField({ label, children }) {
  return (
    <div className="v3-form-field">
      <label className="v3-form-field-label">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
      </div>
    </div>
  );
}
